/**
 * KPI考核 - 人员业绩录入列表
 * 列表形式：筛选条件 + 数据表格 + 录入操作
 */
import React, { useMemo } from 'react';
import { Table, Button, Select, Space, Tag, Modal, InputNumber, Card, Statistic, Row, Col } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useKPIAssessment } from '../hooks/useKPIAssessment';
import type { KPIDimension, MemberPerformanceRecord } from '../../../services/administrationService';
import type { Member } from '../../../services/organizationAPI';

const KPIAssessment: React.FC = () => {
    const {
        records, periods, departments, members, dimensions, targets,
        selectedPeriod, selectedDept, selectedMember,
        setSelectedPeriod, setSelectedDept, setSelectedMember,
        modalVisible, recordingMember, recordValues, saving,
        loading, loadRecords, openRecordModal, setRecordValues, handleSaveRecord, setModalVisible
    } = useKPIAssessment();

    // 按成员聚合记录
    const memberSummaries = useMemo(() => {
        const summaryMap: Record<number, { member: Member; totalScore: number; records: MemberPerformanceRecord[] }> = {};
        records.forEach(r => {
            if (!summaryMap[r.member]) {
                const member = members.find(m => m.id === r.member);
                summaryMap[r.member] = {
                    member: member || { id: r.member, username: r.member_name } as Member,
                    totalScore: 0,
                    records: []
                };
            }
            summaryMap[r.member].totalScore += r.score;
            summaryMap[r.member].records.push(r);
        });
        return Object.values(summaryMap).sort((a, b) => b.totalScore - a.totalScore);
    }, [records, members]);

    const columns = useMemo(() => [
        { title: '排名', key: 'rank', width: 60, render: (_: any, __: any, index: number) => index + 1 },
        { title: '考核周期', key: 'period', width: 120, render: () => periods.find(p => p.id === selectedPeriod)?.name || '-' },
        { title: '部门', key: 'department', width: 120, render: () => departments.find(d => d.id === selectedDept)?.name || '-' },
        {
            title: '成员', key: 'member', width: 150,
            render: (_: any, row: typeof memberSummaries[0]) => (
                <span>{row.member.last_name || ''}{row.member.first_name || ''} ({row.member.username})</span>
            )
        },
        {
            title: '总得分', key: 'totalScore', width: 100,
            render: (_: any, row: typeof memberSummaries[0]) => (
                <Tag color={row.totalScore >= 80 ? 'green' : row.totalScore >= 60 ? 'orange' : 'red'}>
                    {row.totalScore.toFixed(2)}
                </Tag>
            )
        },
        ...dimensions.map(dim => ({
            title: dim.name, key: `dim_${dim.id}`, width: 120,
            render: (_: any, row: typeof memberSummaries[0]) => {
                const rec = row.records.find(r => r.dimension === dim.id);
                if (!rec) return '-';
                return <span>{rec.actual_value}/{rec.target_value} <Tag style={{ marginLeft: 4 }} color="blue">{rec.score.toFixed(1)}</Tag></span>;
            }
        })),
        {
            title: '操作', key: 'action', width: 100, fixed: 'right' as const,
            render: (_: any, row: typeof memberSummaries[0]) => (
                <Button type="link" size="small" onClick={() => openRecordModal(row.member)}>录入</Button>
            )
        }
    ], [periods, departments, dimensions, selectedPeriod, selectedDept, openRecordModal]);

    const avgScore = memberSummaries.length > 0
        ? (memberSummaries.reduce((a, b) => a + b.totalScore, 0) / memberSummaries.length).toFixed(2) : 0;

    return (
        <div style={{ padding: 24 }}>
            {/* 统计卡片 */}
            <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={6}><Card size="small"><Statistic title="已录入人数" value={memberSummaries.length} suffix={`/ ${members.length}`} /></Card></Col>
                <Col span={6}><Card size="small"><Statistic title="部门平均分" value={avgScore} /></Card></Col>
                <Col span={6}><Card size="small"><Statistic title="最高分" value={memberSummaries[0]?.totalScore.toFixed(2) || 0} /></Card></Col>
                <Col span={6}><Card size="small"><Statistic title="考核维度" value={dimensions.length} /></Card></Col>
            </Row>

            {/* 筛选栏 */}
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
                <Space>
                    <Select style={{ width: 180 }} placeholder="选择周期" value={selectedPeriod} onChange={setSelectedPeriod}>
                        {periods.map(p => (
                            <Select.Option key={p.id} value={p.id}>{p.name} {p.status === 'active' && <Tag color="green">进行中</Tag>}</Select.Option>
                        ))}
                    </Select>
                    <Select style={{ width: 180 }} placeholder="选择部门" value={selectedDept} onChange={(v) => { setSelectedDept(v); setSelectedMember(undefined); }} showSearch optionFilterProp="children">
                        {departments.map(d => <Select.Option key={d.id} value={d.id}>{d.name}</Select.Option>)}
                    </Select>
                    <Select style={{ width: 180 }} placeholder="筛选成员" value={selectedMember} onChange={setSelectedMember} allowClear showSearch optionFilterProp="children">
                        {members.map(m => <Select.Option key={m.id} value={m.id}>{m.last_name || ''}{m.first_name || ''} ({m.username})</Select.Option>)}
                    </Select>
                    <Button icon={<ReloadOutlined />} onClick={loadRecords}>刷新</Button>
                </Space>
            </div>

            {/* 数据表格 */}
            <Table
                dataSource={memberSummaries}
                columns={columns}
                rowKey={(row) => row.member.id}
                loading={loading}
                pagination={{ pageSize: 20 }}
                scroll={{ x: 'max-content' }}
                bordered
                size="middle"
            />

            {/* 录入弹窗 */}
            <RecordModal
                visible={modalVisible}
                member={recordingMember}
                dimensions={dimensions}
                targets={targets}
                recordValues={recordValues}
                setRecordValues={setRecordValues}
                onCancel={() => setModalVisible(false)}
                onOk={handleSaveRecord}
                saving={saving}
            />
        </div>
    );
};

// 录入弹窗组件
interface RecordModalProps {
    visible: boolean;
    member: Member | null;
    dimensions: KPIDimension[];
    targets: { dimension: number; target_value: number }[];
    recordValues: Record<number, number>;
    setRecordValues: React.Dispatch<React.SetStateAction<Record<number, number>>>;
    onCancel: () => void;
    onOk: () => void;
    saving: boolean;
}

const RecordModal: React.FC<RecordModalProps> = ({
    visible, member, dimensions, targets, recordValues, setRecordValues, onCancel, onOk, saving
}) => (
    <Modal
        title={`录入业绩 - ${member?.last_name || ''}${member?.first_name || ''}`}
        open={visible}
        onCancel={onCancel}
        onOk={onOk}
        confirmLoading={saving}
        width={600}
    >
        <Table
            dataSource={dimensions}
            rowKey="id"
            pagination={false}
            size="small"
            columns={[
                { title: '维度', dataIndex: 'name', width: 150 },
                { title: '单位', dataIndex: 'unit', width: 80 },
                { title: '目标值', key: 'target', width: 100, render: (_, dim) => targets.find(t => t.dimension === dim.id)?.target_value ?? '-' },
                {
                    title: '实际值', key: 'actual', width: 150,
                    render: (_, dim) => (
                        <InputNumber
                            style={{ width: '100%' }}
                            value={recordValues[dim.id]}
                            onChange={(v) => setRecordValues(prev => ({ ...prev, [dim.id]: v || 0 }))}
                            precision={2}
                        />
                    )
                }
            ]}
        />
    </Modal>
);

export default KPIAssessment;
